import {
  CheckCircleOutlined,
  CreditCardFilled,
  LockOutlined,
  MobileOutlined,
  SecurityScanOutlined,
} from '@ant-design/icons';
import { CardActions } from '@portal/actions/index';
import CardFrontPJ from '@portal/assets/img/card-front-pj.png';
import CardFront from '@portal/assets/img/card_front.png';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import useReduxState from '@portal/hooks/useReduxState';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { AccountDockSolicitationStatus } from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import MessageService from '@portal/services/message';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

const CreateAccountDock = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    user: { me },
    cards: { accountDockStatus },
  } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPF = me?.account?.type === AccountTaxIdentifierType.PERSON;

  const cardImage = useMemo(
    () => `url(${isPF ? CardFront : CardFrontPJ})`,
    [isPF]
  );

  const isPendingOrError = useMemo(
    () =>
      accountDockStatus?.status === AccountDockSolicitationStatus.PENDING ||
      accountDockStatus?.status === AccountDockSolicitationStatus.FAILED,
    [accountDockStatus]
  );

  const benefits = useMemo(
    () => [
      {
        icon: <CreditCardFilled />,
        titleKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.FUNCTION_DEBIT.TITLE',
        descriptionKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.FUNCTION_DEBIT.DESCRIPTION',
      },
      {
        icon: <LockOutlined />,
        titleKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.CONTROL_APP.TITLE',
        descriptionKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.CONTROL_APP.DESCRIPTION',
      },
      {
        icon: <SecurityScanOutlined />,
        titleKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.SECURITY_AND_AUTONOMY.TITLE',
        descriptionKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.SECURITY_AND_AUTONOMY.DESCRIPTION',
      },
      {
        icon: <MobileOutlined />,
        titleKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.MANAGEMENT_SIMPLE.TITLE',
        descriptionKey:
          'PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.BENEFITS.MANAGEMENT_SIMPLE.DESCRIPTION',
      },
    ],
    []
  );

  const handleBackToHome = () => {
    history.push(getRouteStackPath('HOME', 'HOME_PAGE'));
  };

  const handleRequest = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    dispatch(
      CardActions.createAccountDock((err) => {
        setIsSubmitting(false);

        if (err) {
          MessageService.error(
            'PAGES.CARDS.MY_CARDS.ACCOUNT.MESSAGES.CREATE_ACCOUNT_DOCK_ERROR'
          );

          return;
        }

        MessageService.success(
          'PAGES.CARDS.MY_CARDS.ACCOUNT.MESSAGES.CREATE_ACCOUNT_DOCK_SUCCESS'
        );
        handleBackToHome();
      })
    );
  };

  const EmptyState = () => (
    <div className="my-cards__empty">
      <ThemedText variant="h3">
        {translate('PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.TITLE')}
      </ThemedText>
      <ThemedText variant="body" className="my-cards__benefit__description">
        {translate('PAGES.CARDS.MY_CARDS.ACCOUNT.REQUEST.DESCRIPTION')}
      </ThemedText>
      <div className="my-cards__empty__actions">
        <AdvancedButton
          text={translate('PAGES.CARDS.MY_CARDS.ACCOUNT.BUTTONS.REQUEST')}
          variant="contained"
          onClick={handleRequest}
          disabled={isSubmitting}
        />
      </div>
      <div className="my-cards__benefit">
        {benefits.map(({ icon, titleKey, descriptionKey }) => (
          <div className="my-cards__benefit__item" key={titleKey}>
            <div className="my-cards__benefit__icon">{icon}</div>
            <div className="my-cards__benefit__content">
              <div className="my-cards__benefit__title">
                {translate(titleKey)}
              </div>
              <div className="my-cards__benefit__description">
                {translate(descriptionKey)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const PendingState = () => {
    const steps = [
      {
        icon: <CheckCircleOutlined />,
        description: translate(
          'PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.STEPS.ONE'
        ),
      },
      {
        icon: <CheckCircleOutlined />,
        description: translate(
          'PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.STEPS.TWO'
        ),
      },
      {
        icon: <CheckCircleOutlined />,
        description: translate(
          'PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.STEPS.THREE'
        ),
      },
    ];

    return (
      <div className="my-cards__empty">
        <ThemedText variant="h3">
          {translate('PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.TITLE')}
        </ThemedText>
        <ThemedText variant="body" className="my-cards__benefit__description">
          {translate('PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.DESCRIPTION')}
        </ThemedText>
        <div className="my-cards__account__pending">
          <ThemedText className="my-cards__account__pending__title">
            {translate('PAGES.CARDS.MY_CARDS.ACCOUNT.PENDING.QUESTION')}
          </ThemedText>
          {steps.map((step) => (
            <div key={step.description}>
              <div className="my-cards__account__pending__icon">
                {step.icon}
              </div>
              <ThemedText className="my-cards__account__pending__step">
                {step.description}
              </ThemedText>
            </div>
          ))}
        </div>
        <AdvancedButton
          text={translate('PAGES.CARDS.MY_CARDS.ACCOUNT.BUTTONS.BACK_TO_HOME')}
          variant="contained"
          onClick={handleBackToHome}
        />
      </div>
    );
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.CARDS.MY_CARDS.ACCOUNT.TITLE'),
          },
        ]}
      />
      <div className="my-cards">
        <AdvancedCard testId="has-no-create-account-card">
          <div className="my-cards__account">
            <div
              className="my-cards__card-row__image"
              style={{ backgroundImage: cardImage }}
            />
          </div>
          {isPendingOrError ? <PendingState /> : <EmptyState />}
        </AdvancedCard>
      </div>
    </>
  );
};

export default CreateAccountDock;
